import { useEffect, useCallback, useState } from 'react'

interface LightboxImage {
  url: string
  id?: string
  shotNumber?: number
}

interface ImageLightboxProps {
  isOpen: boolean
  onClose: () => void
  images: LightboxImage[]
  initialIndex?: number
}

export function ImageLightbox({
  isOpen,
  onClose,
  images,
  initialIndex = 0,
}: ImageLightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)

  // Reset index when images change or lightbox opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex)
    }
  }, [isOpen, initialIndex])

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return

      switch (e.key) {
        case 'Escape':
          onClose()
          break
        case 'ArrowLeft':
          setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1))
          break
        case 'ArrowRight':
          setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0))
          break
      }
    },
    [isOpen, onClose, images.length]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const handleDownload = () => {
    const currentImage = images[currentIndex]
    if (!currentImage) return

    const link = document.createElement('a')
    link.href = currentImage.url
    link.download = `kalypso-${currentImage.id || 'image'}-${currentImage.shotNumber || currentIndex + 1}.png`
    link.click()
  }

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1))
  }

  const goToNext = () => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0))
  }

  if (!isOpen || images.length === 0) return null

  const currentImage = images[currentIndex]

  return (
    <div className="fixed inset-0 z-[100] lightbox-enter">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/90 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors group"
        aria-label="Close lightbox"
      >
        <span className="material-symbols-outlined text-white text-2xl group-hover:scale-110 transition-transform">
          close
        </span>
      </button>

      {/* Main Image Container */}
      <div className="absolute inset-0 flex items-center justify-center p-4 md:p-8">
        <img
          src={currentImage.url}
          alt={`Image ${currentIndex + 1}`}
          className="max-w-[90vw] max-h-[90vh] object-contain lightbox-image-enter rounded-lg shadow-2xl"
        />
      </div>

      {/* Navigation Arrows (only show if multiple images) */}
      {images.length > 1 && (
        <>
          {/* Previous Button */}
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors group"
            aria-label="Previous image"
          >
            <span className="material-symbols-outlined text-white text-2xl group-hover:scale-110 transition-transform">
              chevron_left
            </span>
          </button>

          {/* Next Button */}
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors group"
            aria-label="Next image"
          >
            <span className="material-symbols-outlined text-white text-2xl group-hover:scale-110 transition-transform">
              chevron_right
            </span>
          </button>

          {/* Image Counter */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm">
            <span className="text-white text-sm font-medium">
              {currentIndex + 1} / {images.length}
            </span>
          </div>
        </>
      )}

      {/* Bottom Controls */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3">
        {/* Shot Number Badge */}
        {currentImage.shotNumber && (
          <span className="px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-medium">
            Shot {currentImage.shotNumber}
          </span>
        )}

        {/* Download Button */}
        <button
          onClick={handleDownload}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-white hover:bg-gray-100 transition-colors text-text-main font-medium text-sm shadow-lg"
        >
          <span className="material-symbols-outlined text-lg">download</span>
          Download
        </button>
      </div>

      {/* Thumbnail Strip (for multiple images) */}
      {images.length > 1 && (
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 p-2 rounded-xl bg-white/10 backdrop-blur-sm">
          {images.map((image, index) => (
            <button
              key={image.id || index}
              onClick={() => setCurrentIndex(index)}
              className={`w-12 h-12 rounded-lg overflow-hidden transition-all ${
                index === currentIndex
                  ? 'ring-2 ring-white scale-110'
                  : 'opacity-60 hover:opacity-100'
              }`}
            >
              <img
                src={image.url}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
